import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '@chakra-ui/react';
import logger from '../utils/logger';

// Define the sentence type based on the Play component's needs
interface Sentence {
  id: string;
  word_id: string;
  sentence: string;
  translation: string;
  translation_lang?: string;
  times_seen: number;
  remembered: boolean;
  word: string;
  language: string;
  words?: { word: string, language: string };
  points: number;
  // SRS fields - make these required since we're using them in the UI
  srs_interval: number;
  srs_repetition: number;
  srs_easiness: number;
  srs_due_date?: string;
  srs_last_reviewed?: string;
  srs_review_count: number;
}

interface NavigationState {
  currentSentence: Sentence | null;
  sentenceHistory: Sentence[];
  historyIndex: number;
  direction: 'forward' | 'backward' | 'none';
  animate: boolean;
}

interface NavigationActions {
  goToNext: () => void;
  goBack: () => void;
  resetAnimationState: () => void;
  handleAfterAnimation: () => void;
  canGoBack: boolean;
}

/**
 * Custom hook to manage navigation in the Play view
 * @param allSentences All available sentences to navigate through
 * @param resetUIState Callback to reset UI state when navigating
 * @returns Navigation state and actions
 */
export function usePlayNavigation(
  allSentences: Sentence[] | null | undefined,
  resetUIState: () => void,
): [NavigationState, NavigationActions] {
  const [sentenceHistory, setSentenceHistory] = useState<Sentence[]>([]);
  const [historyIndex, setHistoryIndex] = useState<number>(-1);
  const [currentSentence, setCurrentSentence] = useState<Sentence | null>(null);
  // For animation direction
  const [direction, setDirection] = useState<'forward' | 'backward' | 'none'>('none');
  const [animate, setAnimate] = useState(false);

  const { t } = useTranslation();
  const toast = useToast();

  const canGoBack = historyIndex > 0;

  // Function to reset animation state
  const resetAnimationState = useCallback(() => {
    logger.debug('Resetting animation state (animate: false)');
    setAnimate(false);
    // Keep the direction set by goToNext/goBack
    // setDirection('none');
  }, []);

  // Function to fetch the next sentence
  const goToNext = useCallback(() => {
    if (animate) {
      logger.debug('[goToNext] Animation already in progress');
      return;
    }
    if (!allSentences || allSentences.length === 0) {
      toast({ title: t('common.error'), description: t('play.noMoreSentences'), status: 'warning' });
      return;
    }

    logger.debug('[goToNext] Proceeding...');
    setDirection('forward');
    setAnimate(true);

    let nextSentence: Sentence | null = null;
    let nextIndex = historyIndex;

    if (historyIndex < sentenceHistory.length - 1) {
      // Move forward in existing history
      nextIndex = historyIndex + 1;
      nextSentence = sentenceHistory[nextIndex];
      logger.debug(`[goToNext] Moving forward in history to index: ${nextIndex}, ID: ${nextSentence?.id}`);
    } else {
      // Add new random sentence to history
      logger.debug('[goToNext] Reached end of history, selecting new random sentence');

      // Get the last 5 sentences to avoid immediate repetition (increased from 3)
      const recentSentenceIds = sentenceHistory.slice(-5).map(s => s.id);

      // CRITICAL FIX: If we have a current sentence, ALWAYS exclude it from selection
      // This is our primary defense against repetition
      if (currentSentence) {
        recentSentenceIds.push(currentSentence.id);
      }

      // Filter out recently seen sentences to prevent immediate repetition
      let availableSentences = allSentences.filter(s => !recentSentenceIds.includes(s.id));

      // If no available sentences after filtering, use all sentences but ALWAYS exclude current
      if (availableSentences.length === 0) {
        availableSentences = allSentences.filter(s => s.id !== currentSentence?.id);
      }

      // Additional safety: if we still don't have any sentences, fall back to all
      // This should only happen if there's only 1 sentence total in the system
      if (availableSentences.length === 0) {
        availableSentences = allSentences;
      }

      let attempts = 0;
      const maxAttempts = Math.min(30, availableSentences.length * 2); // Increased attempts

      // ENHANCED SELECTION: Try to select a sentence that's definitely different
      while (attempts < maxAttempts && availableSentences.length > 0) {
        const randomIndex = Math.floor(Math.random() * availableSentences.length);
        const candidateSentence = availableSentences[randomIndex];

        // Multiple checks to ensure it's different
        const isDifferentFromCurrent = !currentSentence || candidateSentence.id !== currentSentence.id;
        const isDifferentFromRecent = !recentSentenceIds.includes(candidateSentence.id);

        if (isDifferentFromCurrent && isDifferentFromRecent) {
          nextSentence = candidateSentence;
          break;
        }

        // If this candidate is the same as current, remove it from available sentences
        if (currentSentence && candidateSentence.id === currentSentence.id) {
          availableSentences = availableSentences.filter(s => s.id !== currentSentence.id);
        }

        attempts++;
      }

      // Final fallback: if we still couldn't find a different sentence,
      // force select the first available one that's not the current sentence
      if (!nextSentence && availableSentences.length > 0) {
        if (currentSentence) {
          const differentSentences = availableSentences.filter(s => s.id !== currentSentence.id);
          nextSentence = differentSentences.length > 0 ? differentSentences[0] : availableSentences[0];
        } else {
          nextSentence = availableSentences[0];
        }
      }

      // Emergency fallback: if somehow we still don't have a sentence
      if (!nextSentence && allSentences.length > 0) {
        // This should never happen, but if it does, just select the first sentence
        nextSentence = allSentences[0];
        logger.warn('[goToNext] Emergency fallback: selected first sentence from all sentences');
      }

      nextIndex = historyIndex + 1;
      logger.debug(`[goToNext] Selected sentence ID: ${nextSentence?.id}, avoiding recent IDs: [${recentSentenceIds.join(', ')}], attempts: ${attempts}`);
      setSentenceHistory(prev => [...prev, nextSentence as Sentence]);
    }

    setCurrentSentence(nextSentence);
    setHistoryIndex(nextIndex);
    resetUIState(); // Reset UI as soon as we navigate

  }, [animate, allSentences, historyIndex, sentenceHistory, currentSentence, t, toast, resetUIState]);

  // Function to go back to previous sentence
  const goBack = useCallback(() => {
    if (animate) {
      logger.debug('[goBack] Animation already in progress');
      return;
    }
    if (historyIndex <= 0) {
      logger.debug('[goBack] Already at first sentence');
      return;
    }

    logger.debug('[goBack] Proceeding...');
    setDirection('backward');
    setAnimate(true);

    const prevIndex = historyIndex - 1;
    const prevSentence = sentenceHistory[prevIndex];
    logger.debug(`[goBack] Moving backward in history to index: ${prevIndex}, ID: ${prevSentence?.id}`);

    setCurrentSentence(prevSentence);
    setHistoryIndex(prevIndex);
    resetUIState(); // Reset UI as soon as we navigate

  }, [animate, historyIndex, sentenceHistory, resetUIState]);

  // Handle state update after animation completes - Now only resets animation state
  const handleAfterAnimation = useCallback(() => {
    logger.debug('Animation exit completed, calling resetAnimationState');
    resetAnimationState();
  }, [resetAnimationState]);

  // Initialize the first sentence when data loads
  useEffect(() => {
    if (allSentences && allSentences.length > 0 && historyIndex === -1) {
      const randomIndex = Math.floor(Math.random() * allSentences.length);
      const firstSentence = allSentences[randomIndex];
      setCurrentSentence(firstSentence);
      setSentenceHistory([firstSentence]);
      setHistoryIndex(0);
    }
  }, [allSentences, historyIndex]);

  // Return navigation state and actions
  return [
    { currentSentence, sentenceHistory, historyIndex, direction, animate },
    { goToNext, goBack, resetAnimationState, handleAfterAnimation, canGoBack }
  ];
}